import { useRef, useState } from "react";
import { match } from "ts-pattern";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { invalidateQueriesFor, useApiConfig } from "@visioni/a3s-client-api/app";
import type { AssetCameraKey, SiteId } from "@visioni/a3s-schema";
import type { AlarmControlView, PendingIntentKind } from "../alarm-control";
import { interpret, type AlarmControlCommand, type MutationRequest } from "../alarm-control/commands";
import { clearAlarmOverrideMutation, setAlarmOverrideMutation, setAlarmSilenceMutation } from "../commands";
import { alarmConfigurationResource } from "../../client-messages/alarms";

export type UseAlarmControlDispatchInput = {
  readonly siteId: SiteId;
  readonly assetCameraKey: AssetCameraKey;
  /**
   * Called after a mutation fails. The host is expected to surface the
   * failure (typically via `showErrorToast`); `useAlarmControlDispatch`
   * itself does not import any app-level presentation helpers so the
   * feature package stays toast-free. The `command` arg carries the
   * original intent so the toast copy can be specific ("Disarm failed"
   * rather than a generic "Request failed").
   */
  readonly onError?:
    | ((failure: { readonly command: AlarmControlCommand; readonly error: unknown }) => void)
    | undefined;
  /**
   * Called after a mutation succeeds, before the cache is invalidated. The
   * typical use is a `showSuccessToast` for commands whose outcome isn't
   * obvious from the next state (e.g. `resume`).
   */
  readonly onSuccess?: ((command: AlarmControlCommand) => void) | undefined;
};

export type AlarmControlDispatchResult = {
  /**
   * Fire an {@link AlarmControlCommand} given the current view. Returns a
   * promise that resolves on server ack and rejects on server error. The
   * caller passes the latest `view` so `interpret` can reject invalid
   * `(view, command)` pairs; this also breaks the circular dependency
   * between dispatch (which needs the view) and
   * {@link useAlarmControlView} (which reads `pendingIntent`).
   */
  readonly dispatch: (view: AlarmControlView, command: AlarmControlCommand) => Promise<void>;
  /**
   * The intent of the currently in-flight command, or `null` when none.
   * Feed this into `useAlarmControlView` so it can wrap the derived view
   * in a `pending` variant and render the spinner on the correct button.
   */
  readonly pendingIntent: PendingIntentKind | null;
};

/**
 * Turn an `AlarmControlCommand` into the appropriate HTTP mutation.
 *
 * 1. The pure `interpret(view, command, now)` decides which mutation to fire
 *    (or returns `noop` if the pair is invalid — dropped with a console warn).
 * 2. The matching `mutationFor(...)` hook from `../commands.ts` fires the
 *    request and, on success, invalidates the cached alarm configuration so
 *    `useAlarmControlView` re-derives.
 * 3. `pendingIntent` tracks which command is in flight so the view can render
 *    a spinner on the right button. It's kept on local state (rather than
 *    derived from `useMutation`'s `isPending` flags) because a single
 *    `AlarmControlCommand.kind` isn't recoverable from the *request* the
 *    mutation fires (e.g. both `arm` and `disarm` produce `setOverride`).
 */
export function useAlarmControlDispatch(input: UseAlarmControlDispatchInput): AlarmControlDispatchResult {
  const apiConfig = useApiConfig();
  const queryClient = useQueryClient();
  const [pendingIntent, setPendingIntent] = useState<PendingIntentKind | null>(null);
  // Keep the latest callbacks in a ref so the `dispatch` identity is stable
  // even when callers pass fresh arrow functions each render.
  const callbacksRef = useRef({ onError: input.onError, onSuccess: input.onSuccess });
  callbacksRef.current = { onError: input.onError, onSuccess: input.onSuccess };

  const pathParams = {
    siteId: input.siteId,
    assetCameraKey: input.assetCameraKey,
  } as const;

  const mutationConfig =
    apiConfig.tenant ?
      {
        tenantId: apiConfig.tenant.tenantId,
        apiBaseUrl: apiConfig.tenant.apiBaseUrl,
        authenticatedFetch: apiConfig.authenticatedFetch,
      }
    : { authenticatedFetch: apiConfig.authenticatedFetch };

  const setOverride = useMutation(setAlarmOverrideMutation(mutationConfig, pathParams));
  const clearOverride = useMutation(clearAlarmOverrideMutation(mutationConfig, pathParams));
  const setSilence = useMutation(setAlarmSilenceMutation(mutationConfig, pathParams));

  const dispatch = async (view: AlarmControlView, command: AlarmControlCommand): Promise<void> => {
    const request: MutationRequest = interpret(view, command, new Date());

    // `noop` reaching this layer is a contract violation: the component
    // renders buttons that pair validly with the current view (see
    // `AlarmControl.tsx`'s exhaustive match), and `pending` itself noops
    // every command so double-taps can't produce a second request. If we
    // see a noop here, either the host wired a button to the wrong command
    // or the (view, command) pair drifted out of sync with `interpret`.
    //
    // Bail before touching pending state, `onSuccess`, or the cache — the
    // earlier code flashed the spinner for one render and fired callbacks
    // for a request that never went out.
    if (request.kind === "noop") {
      const message =
        `[useAlarmControlDispatch] noop: ${request.reason} — ` +
        `the component should never dispatch an invalid (view, command) pair. ` +
        `Check the button wiring in AlarmControl.tsx or the variant that produced this command.`;
      if (process.env["NODE_ENV"] !== "production") {
        // Throw in dev so the stack trace pinpoints the offending caller.
        // `dispatch` returns a rejected promise; Storybook `play()` and the
        // app-side host swallow via `.catch(() => undefined)`, so no user-
        // facing crash — just a loud developer-facing signal.
        throw new Error(message);
      }
      console.error(message);
      return;
    }

    setPendingIntent(command.kind);
    try {
      await match(request)
        .returnType<Promise<unknown>>()
        .with({ kind: "setOverride" }, (r) => setOverride.mutateAsync(r.override))
        .with({ kind: "clearOverride" }, () => clearOverride.mutateAsync(undefined))
        .with({ kind: "setSilence" }, (r) => setSilence.mutateAsync(r.silence))
        .exhaustive();

      callbacksRef.current.onSuccess?.(command);
      await queryClient.invalidateQueries(
        invalidateQueriesFor(alarmConfigurationResource, {
          path: {
            ...(apiConfig.tenant ? { tenantId: apiConfig.tenant.tenantId } : {}),
            siteId: input.siteId,
            assetCameraKey: input.assetCameraKey,
          },
        }),
      );
    } catch (error: unknown) {
      callbacksRef.current.onError?.({ command, error });
      throw error;
    } finally {
      setPendingIntent(null);
    }
  };

  // Dispatch is passed as a plain closure rather than a `useCallback`: the
  // feature package currently runs without React Compiler, but `dispatch`
  // is only ever invoked from event handlers (never a `useEffect` dep) so
  // stable identity buys nothing. Consumers of `pendingIntent` re-render
  // on intent flips, which is what they want.
  return { dispatch, pendingIntent };
}
